import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import type { CriarTratamentoTemplateDtoEstrutura, ErroResponse } from '@/api/generated/model';
import {
  getTratamentoTemplatesControllerListarQueryKey,
  useTratamentoTemplatesControllerCriar,
} from '@/api/generated/tratamentos/tratamentos';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  estrutura: CriarTratamentoTemplateDtoEstrutura;
  /** Vira `tituloSugerido` — o título deste tratamento costuma servir ao próximo. */
  tituloSugerido: string;
  /** Quantos fármacos do editor NÃO cabem no modelo. Zero esconde o aviso. */
  fora: number;
}

/**
 * SALVAR O QUE ESTÁ NA TELA COMO MODELO.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ **Não existe tela de "cadastrar modelo do zero", e é deliberado.**
 *
 *  Um modelo nasce de um tratamento que o veterinário acabou de montar para um
 *  paciente real — é ali que ele sabe quais fármacos, em que via e em que
 *  frequência. Uma tela separada de cadastro obrigaria a redigitar tudo fora do
 *  contexto clínico, e o resultado é o mesmo de sempre: ninguém cadastra, o
 *  menu fica vazio, e o recurso morre.
 *
 *  Salvar daqui custa um clique sobre trabalho que já está feito.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function SalvarModeloDialogo({ aberto, aoFechar, estrutura, tituloSugerido, fora }: Props) {
  const [nome, setNome] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const criar = useTratamentoTemplatesControllerCriar({
    mutation: {
      onSuccess: async (modelo) => {
        await queryClient.invalidateQueries({
          queryKey: getTratamentoTemplatesControllerListarQueryKey(),
        });
        toast.success(`Modelo "${modelo.nome}" salvo.`);
        fechar();
      },
      onError: (resposta: ErroResponse) => {
        // A mensagem do servidor é escrita em linguagem clínica — em especial a
        // do 409 `TEMPLATE_DUPLICADO`, que diz que já existe um modelo seu com
        // esse nome. Trocá-la por "Erro ao salvar" joga fora a informação.
        setErro(resposta?.erro?.mensagem ?? 'Não foi possível salvar o modelo.');
      },
    },
  });

  function fechar() {
    setNome('');
    setErro(null);
    aoFechar();
  }

  function salvar() {
    const limpo = nome.trim();
    if (!limpo) {
      setErro('Dê um nome ao modelo.');
      return;
    }

    setErro(null);
    criar.mutate({
      data: {
        nome: limpo,
        tituloSugerido: tituloSugerido.trim() || null,
        estrutura,
      },
    });
  }

  return (
    <Dialog open={aberto} onOpenChange={(estado) => !estado && fechar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Salvar como modelo</DialogTitle>
          <DialogDescription>
            Guarda a ESTRUTURA — quais fármacos, por que via e em que frequência. A dose vai como
            sugestão, e quem confirma é sempre você, no próximo paciente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="nome-do-modelo">Nome do modelo</Label>
            <Input
              id="nome-do-modelo"
              value={nome}
              autoFocus
              maxLength={120}
              placeholder="ex.: HAC — trilostano"
              onChange={(evento) => setNome(evento.target.value)}
              onKeyDown={(evento) => {
                if (evento.key === 'Enter') {
                  evento.preventDefault();
                  salvar();
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              É só seu — cada veterinário tem os próprios modelos.
            </p>
          </div>

          <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {estrutura.farmacos.length === 1
              ? '1 fármaco entra no modelo.'
              : `${estrutura.farmacos.length} fármacos entram no modelo.`}
            {fora > 0 && (
              <>
                {' '}
                <span className="text-foreground">
                  {fora === 1
                    ? '1 linha fica de fora por não ter fármaco ou unidade.'
                    : `${fora} linhas ficam de fora por não terem fármaco ou unidade.`}
                </span>
              </>
            )}
          </p>

          {erro && (
            <p
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-foreground"
            >
              {erro}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={fechar} disabled={criar.isPending}>
            Cancelar
          </Button>
          <Button type="button" onClick={salvar} disabled={criar.isPending}>
            {criar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar modelo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
